<script setup lang="ts">
import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
import {
  getCreatedSchoolSubjectId,
  resolveQuickEntryDialogPreselection,
  type IQuickEntryDialogShowParams,
  type TQuickEntryCreatedPayload,
  type TQuickEntryPostCreateAction,
} from '@/composables/quickEntry/quickEntry.utils';
import {
  GrowthEntryTriggerDialog,
  useGrowthEntryPostCreationFlow,
} from '@/composables/growth/useGrowthEntryPostCreationFlow';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useSchoolSubjectGroup } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import { useLastViewed } from '@/composables/useLastViewed';
import { usePerformanceAssessment } from '@/composables/usePerformanceAssessment';
import { useMobile } from '@/composables/useMobile';
import { useToast } from '@/composables/useToast';
import NoteDetailsDialog from '@/core/dialogs/note-details/NoteDetailsDialog.vue';
import PerformanceAssessmentEntryDetailDialog from '@/core/dialogs/performance-assessment-entry-detail/PerformanceAssessmentEntryDetailDialog.vue';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import { DialogQueuePriority } from '@/core/dialogsystem/dialogQueue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { useSupportMeasureEntryDialog } from '@/composables/supportMeasureEntry/useSupportMeasureEntryDialog';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import QuickEntryComposer from './QuickEntryComposer.vue';

const { t } = useI18n();
const { actionSuccessToast } = useToast();
const { lastViewedPerformanceAssessmentId, setLastViewedSchoolSubjectId } = useLastViewed();
const { allPerformanceAssessmentsTeacher } = usePerformanceAssessment();
const { allSchoolSubjects } = useSchoolSubject();
const { allSchoolSubjectGroups } = useSchoolSubjectGroup();
const { isMobile } = useMobile();

const isShown = ref(false);
const growthDetailsOpen = ref(false);
const showAsMobileViewport = ref(false);
const showParams = ref<IQuickEntryDialogShowParams>({});
const wasFirstEntryAtCreationStart = ref(false);
const nativeDialogRef = useTemplateRef('nativeDialogRef');
const composerRef = useTemplateRef('composerRef');

async function show(params: IQuickEntryDialogShowParams = {}): Promise<void> {
  showParams.value = params;
  wasFirstEntryAtCreationStart.value = false;
  showAsMobileViewport.value = isMobile.value;
  isShown.value = true;
  await nextTick();
  if (!showAsMobileViewport.value) nativeDialogRef.value?.showModal();
  // showModal focuses the first focusable chip; the text input is the intended target
  composerRef.value?.focusInput();
}

const growthEntryPostCreationFlow = useGrowthEntryPostCreationFlow({
  onCreateAnotherEntry: () => {
    if (showParams.value.growthFlow) void show(showParams.value);
  },
  triggerDialog: GrowthEntryTriggerDialog.QuickEntry,
});

const handleCreatedEntryDetailsClose = async (): Promise<void> => {
  growthDetailsOpen.value = false;
  await growthEntryPostCreationFlow.handleDetailsClose();
};

const { showSupportMeasureEntryDialog, showSupportMeasureEntryDialogQueued } = useSupportMeasureEntryDialog({
  onClose: handleCreatedEntryDetailsClose,
});

// Registered alongside this dialog so created-entry details do not depend on page-level registrations.
const performanceAssessmentEntryDetailDialog = useDialog({
  component: PerformanceAssessmentEntryDetailDialog,
  props: computed(() => ({
    highlightUnevaluated: !growthDetailsOpen.value,
    onClose: handleCreatedEntryDetailsClose,
  })),
});
const noteDetailsDialog = useDialog({
  component: NoteDetailsDialog,
  props: { onClose: handleCreatedEntryDetailsClose },
});

const resolvedPreselection = computed(() =>
  resolveQuickEntryDialogPreselection(showParams.value, {
    schoolSubjects: allSchoolSubjects.value,
    schoolSubjectGroups: allSchoolSubjectGroups.value,
    performanceAssessments: allPerformanceAssessmentsTeacher.value,
    lastViewedPerformanceAssessmentId: lastViewedPerformanceAssessmentId.value,
  }),
);

const hide = (): void => {
  nativeDialogRef.value?.close();
  isShown.value = false;
};

// Escape/backdrop delegate to the composer's escape ladder; it emits 'close' when closing is ok
const handleCloseRequest = (event?: Event): void => {
  void composerRef.value?.requestClose(event);
};

interface ICreatedEntryDetailsActions {
  show: () => void;
  showQueued: () => void;
}

const getCreatedEntryDetailsActions = (payload: TQuickEntryCreatedPayload): ICreatedEntryDetailsActions => {
  if (payload.type === ObservationTimelineEntryType.supportMeasureEntry) {
    return {
      show: () => showSupportMeasureEntryDialog(payload.supportMeasureEntry.id),
      showQueued: () => showSupportMeasureEntryDialogQueued(payload.supportMeasureEntry.id),
    };
  }
  if (payload.type === ObservationTimelineEntryType.note) {
    const params = { noteId: payload.note.id };
    return {
      show: () => void noteDetailsDialog.show(params),
      showQueued: () =>
        void noteDetailsDialog.showQueued(
          { dedupeKey: `note-detail:${payload.note.id}`, priority: DialogQueuePriority.ProductFlow },
          params,
        ),
    };
  }
  const params = { performanceAssessmentEntryId: payload.performanceAssessmentEntry.id };
  return {
    show: () => void performanceAssessmentEntryDetailDialog.show(params),
    showQueued: () =>
      void performanceAssessmentEntryDetailDialog.showQueued(
        {
          dedupeKey: `performance-assessment-entry-detail:${payload.performanceAssessmentEntry.id}`,
          priority: DialogQueuePriority.ProductFlow,
        },
        params,
      ),
  };
};

const openCreatedEntry = (payload: TQuickEntryCreatedPayload, queued = false): void => {
  const actions = getCreatedEntryDetailsActions(payload);
  if (queued) actions.showQueued();
  else actions.show();
};

const handleCreateStart = (): void => {
  wasFirstEntryAtCreationStart.value = showParams.value.growthFlow
    ? growthEntryPostCreationFlow.captureWasFirstEntry()
    : false;
};

const handleCreated = (payload: TQuickEntryCreatedPayload, postCreateAction?: TQuickEntryPostCreateAction): void => {
  const growthFlow = showParams.value.growthFlow;
  // Dialog entries adopt their subject globally; embedded composers keep their host's local filter behavior.
  setLastViewedSchoolSubjectId(getCreatedSchoolSubjectId(payload));
  hide();
  if (growthFlow) {
    growthEntryPostCreationFlow.handleEntryCreated({
      ...growthFlow,
      requestTrialUpsellEligibilityCheck:
        payload.type === ObservationTimelineEntryType.note ||
        payload.type === ObservationTimelineEntryType.supportMeasureEntry,
      wasFirstEntry: wasFirstEntryAtCreationStart.value,
    });
    wasFirstEntryAtCreationStart.value = false;
    growthDetailsOpen.value = true;
  }
  if (
    postCreateAction === 'open-details' ||
    growthFlow ||
    payload.type === ObservationTimelineEntryType.supportMeasureEntry
  ) {
    openCreatedEntry(payload, true);
    return;
  }
  actionSuccessToast({
    message: t('general.createSuccess', { count: 1 }),
    action: { label: t('quickEntry.createdToast.action'), onClick: () => openCreatedEntry(payload) },
  });
};

defineExpose({ show, hide });
</script>

<template>
  <!-- On mobile the composer owns the only native dialog and renders its shared fullscreen
       viewport presentation. Avoid nesting it inside the centered desktop dialog. -->
  <QuickEntryComposer
    v-if="isShown && showAsMobileViewport"
    ref="composerRef"
    variant="dialog"
    :mobile-viewport="showAsMobileViewport"
    :preselection="resolvedPreselection"
    @create-start="handleCreateStart"
    @close="hide"
    @created="handleCreated"
  />

  <!-- naked shell: the composer panel is the dialog visual; overflow stays visible so the
       suggestion dropdown is not clipped; border-0/p-0 keep the backdrop mousedown check exact -->
  <Transition enter-active-class="motion-dialog-fade-enter-active" enter-from-class="motion-dialog-fade-enter-from">
    <NativeDialog
      v-if="isShown && !showAsMobileViewport"
      ref="nativeDialogRef"
      class="w-full max-w-quick-entry overflow-visible border-0 bg-transparent p-0"
      :aria-label="$t('quickEntry.dialogLabel')"
      :close-on-backdrop-click="false"
      :close-on-cancel="false"
      @backdrop-click="handleCloseRequest($event)"
      @cancel="handleCloseRequest($event)"
      @close="isShown = false"
    >
      <QuickEntryComposer
        ref="composerRef"
        variant="dialog"
        :mobile-viewport="showAsMobileViewport"
        :preselection="resolvedPreselection"
        @create-start="handleCreateStart"
        @close="hide"
        @created="handleCreated"
      />
    </NativeDialog>
  </Transition>
</template>
